import { connect, ConnectedProps } from 'react-redux';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';

import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import Form from 'dashboard/layouts/FormLayout/Form';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadAccountStatuses } from 'dashboard/redux/thunk-actions/account-statuses';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isRequired, withMessage } from 'lib/utils/formValidation';

const validate = {
  accountStatus: [withMessage('Please select an account status', isRequired)],
};

interface FormUpdateAccountStatusProps {
  handleClose: () => void;
}

const AccountStatusSelectField = ({
  accountStatuses,
  loadAccountStatuses,
  showMessage,
  submitting,
}: {
  submitting: boolean;
} & PropsFromRedux) => {
  const accountStatusOptions = accountStatuses
    .filter((accountStatus) => {
      return !accountStatus.attributes['archived-at'];
    })
    .map((accountStatus) => {
      return {
        label: accountStatus.attributes.name,
        value: accountStatus.id,
      };
    });

  const { loading: isLoadingAccountStatuses } = useApiLoader({
    loader: () => loadAccountStatuses(),
    deps: [loadAccountStatuses],
    onError: () => {
      showMessage('Failed to load account statuses.', MessageType.Failure);
    },
    onNetworkError: () =>
      showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
  });

  return (
    <Field
      component={ReactSelectField}
      id='form-update-account-status__account-status-select'
      name='accountStatusId'
      placeholder={isLoadingAccountStatuses ? 'Loading...' : 'Select a status'}
      disabled={submitting}
      options={accountStatusOptions}
      validate={validate.accountStatus}
      isSearchable={true}
      isClearable={true}
      portal={true}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountStatuses: state.accountStatuses.accountStatuses,
  };
};

const mapDispatchToProps = {
  loadAccountStatuses,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedAccountStatusSelectField = connector(AccountStatusSelectField);

const FormUpdateAccountStatus = ({
  pristine,
  submitting,
  handleClose,
  handleSubmit,
}: FormUpdateAccountStatusProps &
  InjectedFormProps<FormUpdateAccountStatusData, FormUpdateAccountStatusProps>) => {
  return (
    <Form onSubmit={handleSubmit}>
      <ConnectedAccountStatusSelectField submitting={submitting} />

      <HStack equal={true}>
        <Button
          variant='basic'
          id='form-update-account-status__cancel'
          data-test='form-update-account-status__cancel'
          disabled={submitting}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='form-update-account-status__submit'
          disabled={submitting || pristine}
          isPending={submitting}
        >
          Update
        </Button>
      </HStack>
    </Form>
  );
};

export interface FormUpdateAccountStatusData {
  accountStatusId: string;
}

export default reduxForm<FormUpdateAccountStatusData, FormUpdateAccountStatusProps>({
  form: 'updateAccountStatus',
})(FormUpdateAccountStatus);
